<template>
  <div>
    <app-modal content-class="col-4 col-xl-3 p-0" @close="close" overflow>
      <b-card no-body ref="root">
        <b-card-body class="pb-2" :title="isNew ? 'Create New Icon' : 'Update Icon'">
          <b-form-group label="Name*" label-cols="3" label-align="right" :state="$v.pointIcon.name.required">
            <b-input :value="pointIcon.name" @change="update('name', $event)" :disabled="disabled" />
            <template #invalid-feedback v-if="$v.pointIcon.name.$dirty && !$v.pointIcon.name.required">Name required</template>
          </b-form-group>
          <b-form-group label="Icon" label-cols="3" label-align="right" :state="fileState">
            <AppFilesSelector
              v-if="dropRef"
              class="point-icon__file"
              :drop-area="dropRef"
              :mime-filter="allowedMimeTypes"
              :accept-input="allowedMimeTypes"
              :disabled="disabled"
              :max-size="1024 * 20"
              @change="handleFileChange"
              @error="handleFileError"
            >
              <div class="point-icon__file-select">
                <div class="point-icon__icon" v-if="fileSrc">
                  <img :src="fileSrc" alt="" :style="iconStyle" />
                  <!--<div class="point-icon__icon-anchor" :style="iconAnchorStyle"></div>-->
                </div>
                <a href="#" @click.prevent class="point-icon__file-select-link">{{ uploadTitle }}</a>
              </div>
            </AppFilesSelector>
            <template #invalid-feedback v-if="!fileSrc && !fileErrorMessage">Icon required</template>
            <template #invalid-feedback v-if="fileErrorMessage">{{ fileErrorMessage }}</template>
          </b-form-group>

          <b-form-group label="Size" label-cols="3" label-align="right" :state="!$v.pointIcon.size.$error">
            <b-row>
              <b-col cols="6">
                <b-input
                  @change="update('width', $event)"
                  :value="pointIcon.width"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="width"
                  :disabled="disabled"
                />
              </b-col>
              <b-col cols="6">
                <b-input
                  @change="update('height', $event)"
                  :value="pointIcon.height"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="height"
                  :disabled="disabled"
                />
              </b-col>
            </b-row>
            <template #invalid-feedback>
              <div v-if="$v.pointIcon.size.$dirty && $v.pointIcon.size.$error">
                Size required
              </div>
            </template>
          </b-form-group>
          <!--          <b-form-group label="Anchor" label-cols="3" label-align="right" :state="!$v.pointIcon.anchor.$error">
            <b-row>
              <b-col cols="6">
                <b-input
                  @change="update('anchor_x', $event)"
                  :value="pointIcon.anchor_x"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="horizontal"
                  :disabled="disabled"
                />
              </b-col>
              <b-col cols="6">
                <b-input
                  @change="update('anchor_y', $event)"
                  :value="pointIcon.anchor_y"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="vertical"
                  :disabled="disabled"
                />
              </b-col>
            </b-row>
            <template #invalid-feedback>
              <div v-if="$v.pointIcon.anchor.$dirty && $v.pointIcon.anchor.$error">
                Anchor required
              </div>
            </template>
          </b-form-group>-->
        </b-card-body>
        <b-card-footer class="d-flex">
          <b-btn variant="default" @click="destroy" v-if="!isNew" :disabled="disabled">Delete</b-btn>
          <b-btn variant="primary ml-auto" @click="save" :disabled="disabled">Save</b-btn>&nbsp;
          <b-btn variant="default" @click="close">Close</b-btn>
        </b-card-footer>
      </b-card>
    </app-modal>
  </div>
</template>

<script>
import { returnTo } from '@/router/navigation'
import store from '@/store'
import { POINT_ICON_CREATE, POINT_ICON_UPDATE } from '@/store/modules/pointIcon'
import { isLocalId } from '@/utils/Id'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { required, maxValue, minValue } from 'vuelidate/lib/validators'
import { convertServerErrors } from '@/utils/Helpers'
import { HTTP } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'
import AppFilesSelector from '@/components/shared/AppFilesSelector.vue'
import { getIconUrl } from '@/components/leaflet/Icon'
import escape from 'lodash/escape'

function getAnchorBySize(width, height) {
  return { anchor_x: width / 2, anchor_y: height }
}

function calculateInitialSizeAndAnchor(width, height) {
  const maxLimit = 50
  if (width < maxLimit && height < maxLimit) {
    return { width, height, ...getAnchorBySize(width, height) }
  }

  const resultSize = {
    width: 0,
    height: 0
  }

  if (width > height) {
    resultSize.width = maxLimit
    resultSize.height = maxLimit * (height / width)
  } else {
    resultSize.height = maxLimit
    resultSize.width = maxLimit * (width / height)
  }

  return { ...resultSize, ...getAnchorBySize(resultSize.width, resultSize.height) }
}

const LOCKED_ID_LIMIT = 100

export default {
  name: 'PointIcon',
  components: { AppFilesSelector },
  data: () => ({
    buildingEditorEnabled: false,
    pointIconId: null,
    file: null,
    fileSrc: '',
    fileErrorMessage: '',
    serverErrors: {},
    dropRef: null,
    allowedMimeTypes: 'image/png,image/jpeg,image/webp,image/svg+xml'
  }),
  validations() {
    return {
      pointIcon: {
        name: {
          required,
          serverErrors: function() {
            return !this.serverFieldErrors('name')
          }
        },
        height: {
          required,
          maxValue: maxValue(50),
          minValue: minValue(0)
        },
        width: {
          required,
          maxValue: maxValue(50),
          minValue: minValue(0)
        },
        anchor_x: {
          required,
          maxValue: maxValue(50),
          minValue: minValue(0)
        },
        anchor_y: {
          required,
          maxValue: maxValue(50),
          minValue: minValue(0)
        },
        anchor: ['pointIcon.anchor_x', 'pointIcon.anchor_y'],
        size: ['pointIcon.width', 'pointIcon.height']
      }
    }
  },
  computed: {
    ...mapState('auth', { currentPointIcon: 'pointIcon' }),
    ...mapGetters('pointIcon', ['getPointIcon']),
    disabled() {
      return !this.isNew && this.pointIcon.id < LOCKED_ID_LIMIT
    },
    pointIcon() {
      return this.getPointIcon(this.pointIconId) || {}
    },
    isNew() {
      return isLocalId(this.pointIconId)
    },
    hasIcon() {
      return Boolean(this.file || !this.isNew)
    },
    uploadTitle() {
      return this.fileSrc ? 'Update Icon' : 'Upload Icon'
    },
    iconStyle() {
      return {
        width: `${this.pointIcon.width}px`,
        height: `${this.pointIcon.height}px`
      }
    },
    iconAnchorStyle() {
      if (
        this.pointIcon.anchor_y === undefined ||
        this.pointIcon.anchor_y === null ||
        this.pointIcon.anchor_x === undefined ||
        this.pointIcon.anchor_x === null
      ) {
        return { display: 'none' }
      }
      return {
        top: `${+this.pointIcon.anchor_y + 8}px`,
        left: `${this.pointIcon.anchor_x}px`
      }
    },
    fileState() {
      if (this.$v.$dirty) {
        return this.hasIcon
      }
      return null
    }
  },
  methods: {
    ...mapMutations('pointIcon', { updateLocal_: POINT_ICON_UPDATE }),
    ...mapActions('pointIcon', ['pointIconSave', 'pointIconDestroy', 'pointIconLoad']),
    /**
     * @param fieldName
     * @returns {string} merged error messages splited by br tag
     */
    serverFieldErrors(fieldName) {
      if (this.serverErrors && this.serverErrors[fieldName]) {
        return this.serverErrors[fieldName].join('<br>')
      }
      return ''
    },
    buildingEditorToggle() {
      this.buildingEditorEnabled = !this.buildingEditorEnabled
    },
    update(k, v) {
      let value = v instanceof Event ? v.target.value : v
      this.updateLocal_({ id: this.pointIcon.id, [k]: value })
      this.$v.pointIcon[k] && this.$v.pointIcon[k].$touch()
    },
    close() {
      // back when the list is what the modal was opened from - pushing it would leave the
      // list in the history twice and back would reopen the modal
      return returnTo(this.$router, { name: 'point-icons' })
    },

    async save(close = true) {
      this.$v.$touch()
      this.serverErrors = {}
      if (!this.$v.$invalid && this.hasIcon) {
        try {
          const data = { ...this.pointIcon, file_updated_at: this.file ? new Date().toUTCString() : this.pointIcon.file_updated_at }
          const pointIconId = await this.pointIconSave(data)
          this.pointIconId = pointIconId
          if (this.file) {
            const form = new FormData()
            form.set('file', this.file)
            await HTTP.post(`${UrlStore.POINT_ICONS}/${pointIconId}/upload`, form)
            this.file = null
          }
          close && this.close()
        } catch (error) {
          if (error.response && error.response.status === 422) {
            this.serverErrors = convertServerErrors(error.response.data)
          }
        }
      }
    },
    async destroy() {
      try {
        await this.$appToast.question(`Deleting "${escape(this.pointIcon.name)}" icon will remove any of the same icon from your floor plans. Do you want to continue?`)
        await this.pointIconDestroy(this.pointIcon)
        this.close()
      } catch (e) {
        if (e.message === 'canceled') {
          console.error('[PointIcon]:' + e.message)
        } else {
          throw e
        }
      }
    },
    handleFileChange(files) {
      this.fileErrorMessage = ''
      this.file = files[0]
      this.setFileSrc()
    },
    handleFileError(message) {
      this.fileErrorMessage = message
    },
    initFileSize(src) {
      const img = document.createElement('img')
      img.onload = () => {
        const size = calculateInitialSizeAndAnchor(img.width, img.height)
        Object.entries(size).map(([dim, value]) => {
          this.$set(this.pointIcon, dim, Math.round(value))
        })
      }
      img.src = src
    },
    setFileSrc() {
      if (this.file) {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.fileSrc = event.target.result
          this.initFileSize(event.target.result)
        }
        reader.readAsDataURL(this.file)
      } else if (!this.isNew) {
        this.fileSrc = getIconUrl(this.pointIconId, this.pointIcon.file_updated_at)
      }
    }
  },
  mounted() {
    this.dropRef = this.$refs.root
  },
  created() {
    this.pointIconId = this.$route.params.pointIconId
    this.setFileSrc()
  },
  beforeRouteEnter(to, from, next) {
    // create local pointIcon
    let uId = to.params.pointIconId
    if (isLocalId(uId) && !store.getters['pointIcon/getPointIcon'](uId)) {
      store.commit(`pointIcon/${POINT_ICON_CREATE}`, { id: uId })
    } else {
      store.dispatch(`pointIcon/pointIconLoad`, { id: uId })
    }
    next()
  },
  watch: {
    'pointIcon.width': {
      handler() {
        this.pointIcon.anchor_x = Math.round(this.pointIcon.width / 2)
      }
    },
    'pointIcon.height': {
      handler() {
        this.pointIcon.anchor_y = this.pointIcon.height
      }
    }
  }
}
</script>

<style lang="scss">
.point-icon__file {
  &-select {
    margin-top: 8px;
    display: flex;
    align-items: center;
    height: 100%;
  }
}

.point-icon__icon {
  position: relative;
  padding: 8px;
  &-anchor {
    position: absolute;
    border: 1px black solid;
    width: 16px;
    height: 1px;

    &:after {
      content: '';
      width: 1px;
      border: 1px black solid;
      height: 16px;
      display: block;
      margin: auto;
      transform: translateY(-50%);
    }
  }
}
</style>
